import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Stack from '@zenbusiness/zui/Stack';

import { useProductPrice } from '#client/pages/ProductLanding/hooks/useProductPrice';
import { useServiceFlags } from '#client/pages/ProductLanding/hooks/useServiceFlags';

import * as Styled from './BuyBox.style';
import { useBuyBoxContext } from './BuyBoxContext';
import { meetsCouponFlagRequirement } from './coupons/utils';

export const getDisplayPrice = (amount, interval, showZero = false) => {
  if (amount === 0 && !showZero) {
    return '';
  }

  if (typeof amount !== 'number' || Number.isNaN(amount)) {
    return '';
  }

  const formatted = `$${amount}`;
  switch (interval) {
    case 'monthly':
      return `${formatted}/month`;
    case 'annual':
      return `${formatted}/yr`;
    default:
      return formatted;
  }
};

function TitleSection() {
  const {
    ctaProps,
    enabledCoupon,
    interval: intervalFromContext,
    productName,
    priceLabel,
    priceNote,
    serviceCode,
  } = useBuyBoxContext();

  const flagValues = useServiceFlags(serviceCode);

  const couponToApply = meetsCouponFlagRequirement(serviceCode, enabledCoupon, flagValues) ? enabledCoupon : null;

  const { amount, contractInterval, fees, interval, originalAmount } = useProductPrice(
    serviceCode,
    intervalFromContext,
    couponToApply,
  );

  const ownedCtaText = !ctaProps.isBuyEnabled ? ctaProps.ownedCtaText : null;

  const renderPriceDisplay = () => {
    if (ownedCtaText) {
      return <FormattedMessage id={ownedCtaText} />;
    }

    if (couponToApply?.amount) {
      return (
        <Styled.DiscountPriceContainer>
          <Stack direction="row" alignItems="center" gap={1}>
            <Styled.StrikethroughPrice>{getDisplayPrice(originalAmount, interval)}</Styled.StrikethroughPrice>
            <Styled.BuyBoxText color="green">{getDisplayPrice(amount, interval, true)}</Styled.BuyBoxText>
          </Stack>

          {fees && (
            <Styled.FeeText>
              <FormattedMessage id={fees} />
            </Styled.FeeText>
          )}
          {!fees && interval === 'onetime' && amount > 0 && (
            <Styled.FeeText>
              <FormattedMessage id="productPage.common.buyBox.oneTimeFee" />
            </Styled.FeeText>
          )}

          <Styled.CouponMessageText color="green" size="sm" weight="medium">
            <FormattedMessage
              id="productPage.common.buyBox.couponMessage"
              values={{
                couponAmount: Math.max((originalAmount || 0) - (amount || 0), 0),
              }}
            />
          </Styled.CouponMessageText>
        </Styled.DiscountPriceContainer>
      );
    }

    return (
      <>
        <Styled.BuyBoxText>
          {priceLabel ? (
            <>
              {`$${amount} `}
              <FormattedMessage id={priceLabel} />
            </>
          ) : (
            getDisplayPrice(amount, interval)
          )}
        </Styled.BuyBoxText>
        {fees && (
          <Styled.FeeText>
            <FormattedMessage id={fees} />
          </Styled.FeeText>
        )}
        {!fees && interval === 'onetime' && amount > 0 && (
          <Styled.FeeText>
            <FormattedMessage id="productPage.common.buyBox.oneTimeFee" />
          </Styled.FeeText>
        )}
      </>
    );
  };

  const displayPrice = renderPriceDisplay();

  return (
    <Stack flexDirection="row" gap={2} alignItems="center" justifyContent="space-between">
      <Styled.BuyBoxText weight="medium" color="primary">
        <FormattedMessage id={productName} />
      </Styled.BuyBoxText>
      {displayPrice && (
        <Styled.PriceContainer>
          {displayPrice}
          {priceNote ? (
            <Styled.FeeText>
              <FormattedMessage id={priceNote} />
            </Styled.FeeText>
          ) : (
            contractInterval === 'annual' &&
            interval === 'monthly' && (
              <Styled.FeeText>
                <FormattedMessage id="productPage.common.buyBox.annualContractLabel" />
              </Styled.FeeText>
            )
          )}
        </Styled.PriceContainer>
      )}
    </Stack>
  );
}

export default TitleSection;
